import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { api } from '../../services/api';

export interface AuditTrailEntry {
  _id: string;
  tenantId: string;
  personalPerformanceId: string;
  annualTargetId: string;
  quarter: 'Q1' | 'Q2' | 'Q3' | 'Q4';
  employeeId: string;
  employeeName: string;
  employeeEmail?: string;
  changedById: string;
  changedByName: string;
  changedByEmail?: string;
  changedByRole: string;
  entityType: 'agreement' | 'assessment';
  actionType: string;
  fieldName?: string;
  previousValue?: any;
  newValue?: any;
  description: string;
  kpiIndicator?: string;
  objectiveName?: string;
  initiativeName?: string;
  perspectiveName?: string;
  metadata?: Record<string, any>;
  timestamp: string;
}

export interface AuditTrailFilters {
  annualTargetId?: string;
  quarter?: string;
  employeeId?: string;
  changedById?: string;
  entityType?: 'agreement' | 'assessment';
  actionType?: string;
  startDate?: string;
  endDate?: string;
  page?: number;
  limit?: number;
}

export interface AuditTrailState {
  entries: AuditTrailEntry[];
  loading: boolean;
  error: string | null;
  pagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
  filters: AuditTrailFilters;
}

const initialState: AuditTrailState = {
  entries: [],
  loading: false,
  error: null,
  pagination: {
    total: 0,
    page: 1,
    limit: 50,
    totalPages: 0
  },
  filters: {}
};

export const fetchAuditTrails = createAsyncThunk(
  'auditTrail/fetchAuditTrails',
  async (filters: AuditTrailFilters, { rejectWithValue }) => {
    try {
      const response = await api.get('/performance-audit-trail', {
        params: filters
      });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch audit trail');
    }
  }
);

const auditTrailSlice = createSlice({
  name: 'auditTrail',
  initialState,
  reducers: {
    setFilters: (state, action: PayloadAction<AuditTrailFilters>) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearFilters: (state) => {
      state.filters = {};
      state.pagination.page = 1;
    },
    resetPagination: (state) => {
      state.filters.page = 1;
      state.pagination.page = 1;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAuditTrails.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAuditTrails.fulfilled, (state, action) => {
        state.loading = false;
        state.entries = action.payload.data;
        state.pagination = action.payload.pagination;
      })
      .addCase(fetchAuditTrails.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  }
});

export const { setFilters, clearFilters, resetPagination } = auditTrailSlice.actions;
export default auditTrailSlice.reducer;

